import { Modal, message } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { AiOutlineExclamationCircle } from "react-icons/ai"
import { useHistory } from "react-router"
import styled from "styled-components"

import { deleteBooking } from "../../../../../api/index"
import { useStateValue } from "../../../context/AppStateStore"
import useMeasures from "../../../hooks/useMeasure"
import useWindowWidth from "../../../hooks/useWindowWidth"
import AppButton from "../../AppButton"

const { confirm } = Modal

// ----------------------------------------------------------------------------

function _BookingsTable(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { bookings, fetchBookings } = props

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ user }] = useStateValue()

    const vw = useWindowWidth()
    const wrapperRef = useRef(null)
    const [spanWidth, setSpanWidth] = useState(0)

    const history = useHistory()

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        // Set title span max-width based on row width
        // to have correct text-overflow: ellipsis

        const width = getSpanWidth()

        // width - grid-gap * 2
        setSpanWidth(width - 20)

        //eslint-disable-next-line
    }, [vw, wrapperRef.current])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function getSpanWidth() {
        if (!wrapperRef.current) return
        const wrapperWidth = wrapperRef.current.clientWidth
        if (vw >= 780) {
            return (wrapperWidth / 5.5) * 2
        } else if (vw <= 780 && vw >= 576) {
            return (wrapperWidth / 3) * 2
        } else {
            return wrapperWidth
        }
    }

    function showConfirm(booking) {
        confirm({
            title: "Sei sicuro di voler cancellare la tua prenotazione?",
            icon: <AiOutlineExclamationCircle />,
            content:
                "Se cancelli la tua prenotazione, il tuo slot orario sarà disponibile nuovamente per gli altri utenti. Se desideri continuare, scegli 'Cancella prenotazione', altrimenti premi 'Annulla'.",
            onOk: handleDeleteBooking(booking),
        })
    }

    function handleDeleteBooking(booking) {
        return async () => {
            try {
                await deleteBooking(user.id, booking.id)
                message.success("La tua prenotazione è stata cancellata.")
                fetchBookings()
            } catch (e) {
                message.error(e.message)
            }
        }
    }

    function clean(str) {
        return str
            ?.replace("<br>", " ")
            ?.replace(/<p>/g, " ")
            ?.replace(/<\/p>/g, " ")
    }

    function renderRows() {
        return bookings?.map((row) => {
            if (!row.event) {
                return null
            }
            const location = row.event.locations.find(
                (l) => l.code === row.slot.locationCode
            )
            return (
                <div className="ticket" key={row.id}>
                    <div ref={wrapperRef} className="grid-row">
                        <BookingInfo
                            row={row}
                            spanWidth={spanWidth}
                            clean={clean}
                            location={location}
                        />
                        {renderActions(row)}
                    </div>
                    <div className="divider"></div>
                </div>
            )
        })
    }

    function navigateToEvent(event) {
        return () => history.push(`/events/${event.id}`)
    }

    function navigateToBooking(booking) {
        return () => history.push(`/bookings/${booking.id}`)
    }

    function renderActions(row) {
        return (
            <div className="actions">
                <AppButton
                    uxType="primary"
                    htmlType="button"
                    className="link-btn"
                    onClick={navigateToEvent(row.event)}
                >
                    {"Vai all'evento"}
                </AppButton>
                <AppButton
                    uxType="secondary"
                    htmlType="button"
                    className="link-btn"
                    onClick={navigateToBooking(row)}
                >
                    {"Modifica i tuoi dati"}
                </AppButton>
                <AppButton
                    uxType="delete"
                    htmlType="button"
                    className="delete"
                    onClick={() => showConfirm(row)}
                >
                    Cancella prenotazione
                </AppButton>
            </div>
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    if (!user?.id || !bookings?.length) {
        return <></>
    }

    return (
        <div className={props.className}>
            <div className="grid-table">{renderRows()}</div>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_BookingsTable.propTypes = {
    className: PropTypes.string.isRequired,
    bookings: PropTypes.array,
    fetchBookings: PropTypes.func,
}

_BookingsTable.defaultProps = {}

// ----------------------------------------------------------------------------

const BookingsTable = styled(_BookingsTable)`
    & {
        width: 100%;
        margin-top: 25px;
        .grid-table {
            width: 100%;
            .ticket {
                display: flex;
                flex-direction: column;

                .divider {
                    width: 100%;
                    height: 1px;
                    background-color: ${({ theme }) =>
                        theme.colors.dividerColor};
                    margin: 33px 0px;
                }

                .grid-row {
                    display: grid;
                    width: 100%;
                    max-width: 100%;
                    grid-template-columns: minmax(150px, 1fr) 2fr 2.5fr;
                    padding: 0px 20px;
                    grid-gap: 10px;
                    div:not(.slot-col) {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        justify-content: center;
                        font-size: 16px;
                    }
                    .col {
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-start;
                        span {
                            display: block;
                            &:last-child {
                                font-family: "Poppins-Bold";
                                font-size: 18px;
                            }
                        }
                    }
                    .event-col {
                        max-width: 100%;
                        width: 100%;
                    }
                    .event-title {
                        span {
                            display: block;
                            width: 100%;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                        min-width: 150px;
                    }
                    .actions {
                        width: 100%;
                        display: flex;
                        flex-direction: row !important;
                        align-items: center !important;
                        justify-content: flex-end !important;
                        height: 100%;
                        .link-btn,
                        .delete {
                            font-size: 14px !important;
                            height: 50px !important;
                            min-width: 0px;
                            max-width: unset;
                        }
                        .link-btn {
                            margin-right: 20px;
                        }
                    }
                }
            }
        }
        @media (max-width: 1200px) {
            .grid-table {
                .ticket {
                    .grid-row {
                        .actions {
                            width: 100%;
                            display: flex;
                            flex-direction: column !important;
                            align-items: center !important;
                            justify-content: flex-end !important;
                            .link-btn,
                            .delete {
                                font-size: 14px !important;
                                height: 50px !important;
                                min-width: 0px;
                                max-width: unset;
                                width: 100%;
                                margin-bottom: 5px;
                            }
                            .link-btn {
                                margin-right: 0px;
                            }
                        }
                    }
                }
            }
        }
        @media (max-width: 780px) {
            .grid-table {
                .ticket {
                    .grid-row {
                        padding: 0px 10px;
                        grid-template-columns: 1fr 2fr;
                        grid-template-rows: 1fr 1fr;
                        .col.event-col {
                            align-items: flex-end !important;
                            text-align: right;
                            .event-title {
                                align-items: flex-end;
                            }
                        }
                        .actions {
                            flex-direction: column !important;
                            grid-column: 1 / span 2;
                            .link-btn,
                            .delete {
                                width: 100%;
                                max-width: 100%;
                            }
                            .link-btn {
                                margin-right: 0px;
                                margin-bottom: 10px;
                            }
                        }
                    }
                }
            }
        }
        @media (max-width: 576px) {
            .grid-table {
                .ticket {
                    .grid-row {
                        padding: 0;
                        grid-template-columns: none;
                        grid-template-rows: none;
                        .slot-info {
                            grid-row: 1;
                            grid-column: 1;
                            margin-bottom: 20px;
                        }
                        .col.event-col {
                            grid-row: 2;
                            grid-column: 1;
                            align-items: flex-start !important;
                            .event-title {
                                align-items: flex-start;
                            }
                            margin-bottom: 20px;
                        }
                        .actions {
                            flex-direction: column !important;
                            grid-row: 3;
                            grid-column: 1;
                        }
                    }
                }
            }
        }
    }
`
// ----------------------------------------------------------------------------

function BookingInfo({ row, spanWidth, clean, location }) {
    const [measures, bind] = useMeasures()

    return (
        <>
            <div className="slot-info">
                <div className="col">
                    <span
                        style={{
                            display: "block",
                            height: measures.height || 30,
                        }}
                    >
                        {dayjs(row.slot.date).format("DD MMMM YYYY")}
                    </span>
                    <span>{row.slot.window}</span>
                </div>
            </div>
            <div className="col event-col">
                <div className="event-location" {...bind}>
                    {location.address}, {location.city} ({location.province})
                </div>
                <div className="event-title">
                    <span style={{ maxWidth: `${spanWidth}px` }}>
                        {clean(row.event.title)}
                    </span>
                </div>
            </div>
        </>
    )
}

// ----------------------------------------------------------------------------

export default BookingsTable
